import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/auth-context";
import { useLanguage } from "@/contexts/language-context";
import { api } from "@/lib/api";
import { SEOHead } from "@/components/seo/seo-head";
import { showToast } from "@/lib/toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/shared/skeleton";
import { User, Settings as SettingsIcon, DollarSign, Languages, Save, Eye, EyeOff } from "lucide-react";
import { SUPPORTED_CURRENCIES } from "@/lib/currency";
import { SUPPORTED_LANGUAGES } from "@/lib/i18n";

export function SettingsPage() {
  const { user, updateUser } = useAuth();
  const { language, setLanguage: setLanguageState, t } = useLanguage();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [profileForm, setProfileForm] = useState({
    username: "",
    email: "",
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [currency, setCurrency] = useState("IDR");

  useEffect(() => {
    if (user) {
      setProfileForm({
        username: user.username || "",
        email: user.email || "",
      });
      setCurrency(user.currency || "IDR");
    }
    setIsLoading(false);
  }, [user]);

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      const updates: { username?: string; email?: string } = {};
      
      if (profileForm.username !== user?.username) {
        updates.username = profileForm.username;
      }
      
      if (profileForm.email !== user?.email) {
        updates.email = profileForm.email;
      }

      if (Object.keys(updates).length === 0) {
        showToast.info("No changes to save");
        setIsSaving(false);
        return;
      }

      const data = await api.put<{ user: typeof user }>("/users/me", updates);
      if (data.user) {
        updateUser(data.user);
      }
      showToast.success("Profile updated successfully");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to update profile";
      showToast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handlePasswordUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      showToast.error("New passwords do not match");
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      showToast.error("Password must be at least 6 characters");
      return;
    }

    setIsSaving(true);

    try {
      await api.put("/users/me", {
        password: passwordForm.newPassword,
      });
      
      setPasswordForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      showToast.success("Password updated successfully");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to update password";
      showToast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCurrencyUpdate = async (newCurrency: string) => {
    setCurrency(newCurrency);
    setIsSaving(true);

    try {
      const data = await api.put<{ user: typeof user }>("/users/me", {
        currency: newCurrency,
      });
      if (data.user) {
        updateUser(data.user);
      }
      showToast.success(t("settings.success"));
    } catch (error) {
      const message = error instanceof Error ? error.message : t("settings.error");
      showToast.error(message);
      // Revert on error
      setCurrency(user?.currency || "IDR");
    } finally {
      setIsSaving(false);
    }
  };

  const handleLanguageUpdate = async (newLanguage: string) => {
    setIsSaving(true);
    try {
      await setLanguageState(newLanguage as "en" | "id");
      showToast.success(t("settings.success"));
    } catch (error) {
      const message = error instanceof Error ? error.message : t("settings.error");
      showToast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <>
        <SEOHead
          title="Settings - Personal Finance App"
          description="Manage your account settings, update your profile, change currency preferences, language settings, and update your password."
          keywords="settings, account settings, profile, currency, language, preferences"
          canonicalUrl="/settings"
        />
        <div className="space-y-6">
        <div>
          <Skeleton className="h-9 w-48 mb-2" />
          <Skeleton className="h-5 w-64" />
        </div>
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-32" />
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          </CardContent>
        </Card>
      </div>
      </>
    );
  }

  return (
    <>
      <SEOHead
        title="Settings - Personal Finance App"
        description="Manage your account settings, update your profile, change currency preferences, language settings, and update your password."
        keywords="settings, account settings, profile, currency, language, preferences"
        canonicalUrl="/settings"
      />
      <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <SettingsIcon className="h-8 w-8" />
          {t("settings.title")}
        </h1>
        <p className="text-muted-foreground">
          {t("settings.subtitle")}
        </p>
      </div>

      {/* Profile Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            {t("settings.profile")}
          </CardTitle>
          <CardDescription>
            {t("settings.profileDescription")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleProfileUpdate} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username">{t("settings.username")}</Label>
              <Input
                id="username"
                value={profileForm.username}
                onChange={(e) => setProfileForm({ ...profileForm, username: e.target.value })}
                required
                minLength={3}
                disabled={isSaving}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">{t("settings.email")}</Label>
              <Input
                id="email"
                type="email"
                value={profileForm.email}
                onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                required
                disabled={isSaving}
              />
            </div>

            <Button type="submit" disabled={isSaving} className="w-full sm:w-auto">
              <Save className="h-4 w-4 mr-2" />
              {isSaving ? t("settings.saving") : t("settings.save")}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Currency Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            {t("settings.currency")}
          </CardTitle>
          <CardDescription>
            {t("settings.currencyDescription")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="currency">{t("settings.currency")}</Label>
              <Select
                value={currency}
                onValueChange={handleCurrencyUpdate}
                disabled={isSaving}
              >
                <SelectTrigger id="currency" className="w-full sm:w-[300px]">
                  <SelectValue placeholder="Select currency" />
                </SelectTrigger>
                <SelectContent>
                  {SUPPORTED_CURRENCIES.map((curr) => (
                    <SelectItem key={curr.code} value={curr.code}>
                      {curr.symbol} {curr.name} ({curr.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                {t("settings.currencyDescription")}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Language Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Languages className="h-5 w-5" />
            {t("settings.language")}
          </CardTitle>
          <CardDescription>
            {t("settings.languageDescription")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="language">{t("settings.language")}</Label>
              <Select
                value={language}
                onValueChange={handleLanguageUpdate}
                disabled={isSaving}
              >
                <SelectTrigger id="language" className="w-full sm:w-[300px]">
                  <SelectValue placeholder="Select language" />
                </SelectTrigger>
                <SelectContent>
                  {SUPPORTED_LANGUAGES.map((lang) => (
                    <SelectItem key={lang.code} value={lang.code}>
                      {lang.nativeName} ({lang.name})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">
                {t("settings.languageDescription")}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Password Settings */}
      <Card>
        <CardHeader>
          <CardTitle>{t("settings.password")}</CardTitle>
          <CardDescription>
            {t("settings.passwordDescription")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handlePasswordUpdate} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="newPassword">{t("settings.newPassword")}</Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  type={showNewPassword ? "text" : "password"}
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                  required
                  minLength={6}
                  disabled={isSaving}
                  placeholder="At least 6 characters"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded-sm p-1"
                  aria-label={showNewPassword ? "Hide password" : "Show password"}
                  tabIndex={-1}
                >
                  {showNewPassword ? (
                    <EyeOff className="h-4 w-4 sm:h-5 sm:w-5" />
                  ) : (
                    <Eye className="h-4 w-4 sm:h-5 sm:w-5" />
                  )}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">{t("settings.confirmPassword")}</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                  required
                  minLength={6}
                  disabled={isSaving}
                  placeholder="Confirm your new password"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded-sm p-1"
                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                  tabIndex={-1}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-4 w-4 sm:h-5 sm:w-5" />
                  ) : (
                    <Eye className="h-4 w-4 sm:h-5 sm:w-5" />
                  )}
                </button>
              </div>
            </div>

            <Button type="submit" disabled={isSaving} className="w-full sm:w-auto">
              <Save className="h-4 w-4 mr-2" />
              {isSaving ? t("settings.updating") : t("settings.updatePassword")}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
    </>
  );
}
